/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import {
	Box,
	Paper,
	Typography,
	Grid,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Chip,
	IconButton,
	Button,
	Tooltip,
	Collapse,
	FormControlLabel,
	Switch,
	Divider,
	Badge,
} from '@mui/material';
import {
	FilterList as FilterListIcon,
	Clear as ClearIcon,
	ExpandMore as ExpandMoreIcon,
	ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import DatePicker from '@mui/lab/DatePicker';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { ticketingStatusOptions, eticketTypeOptions } from '../constants';
import { countriesWithAgencyConfig } from 'features/auth/constants';

export interface ModernFiltersState {
	vcCode: string;
	saleOrderId: string;
	saleOrderCreatedBy: string;
	saleOrderDealId: string;
	ticketType: string;
	ticketingRequested: boolean | null;
	ticketingStatus: string;
	saleOrderAgencyId: string;
	serviceEnable: boolean | null;
	ticketingLimitDateFrom: Date | null;
	ticketingLimitDateTo: Date | null;
	serviceCreatedFrom: Date | null;
	serviceCreatedTo: Date | null;
}

interface ModernFiltersProps {
	onFiltersChange: (filters: ModernFiltersState) => void;
	onClearFilters: () => void;
	initialFilters?: Partial<ModernFiltersState>;
}

const defaultFilters: ModernFiltersState = {
	vcCode: '',
	saleOrderId: '',
	saleOrderCreatedBy: '',
	saleOrderDealId: '',
	ticketType: '',
	ticketingRequested: true, // Por defecto true
	ticketingStatus: 'pending', // Por defecto pending
	saleOrderAgencyId: '',
	serviceEnable: true, // Por defecto true (solo habilitados)
	ticketingLimitDateFrom: null,
	ticketingLimitDateTo: null,
	serviceCreatedFrom: null,
	serviceCreatedTo: null,
};

export const ModernFilters: React.FC<ModernFiltersProps> = ({
	onFiltersChange,
	onClearFilters,
	initialFilters = {},
}) => {
	const [filters, setFilters] = useState<ModernFiltersState>({
		...defaultFilters,
		...initialFilters,
	});
	const [expanded, setExpanded] = useState(false);
	const [activeFiltersCount, setActiveFiltersCount] = useState(0);
	const [isInitialized, setIsInitialized] = useState(false);

	// Contar filtros activos
	const countActiveFilters = useCallback((currentFilters: ModernFiltersState) => {
		let count = 0;
		Object.entries(currentFilters).forEach(([, value]) => {
			if (value && value !== '' && value !== null) count++;
		});
		return count;
	}, []);

	// Efecto para inicialización
	useEffect(() => {
		if (!isInitialized) {
			console.log('ModernFilters - Inicializando con filtros:', filters);
			const count = countActiveFilters(filters);
			setActiveFiltersCount(count);
			onFiltersChange(filters);
			setIsInitialized(true);
		}
	}, []);

	// Efecto para cambios posteriores a la inicialización
	useEffect(() => {
		if (isInitialized) {
			console.log('ModernFilters - Filtros cambiados:', filters);
			const count = countActiveFilters(filters);
			setActiveFiltersCount(count);
			onFiltersChange(filters);
		}
	}, [filters, countActiveFilters, onFiltersChange, isInitialized]);

	const handleFilterChange = (field: keyof ModernFiltersState, value: any) => {
		setFilters((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	const handleClearAll = () => {
		const clearedFilters: ModernFiltersState = {
			vcCode: '',
			saleOrderId: '',
			saleOrderCreatedBy: '',
			saleOrderDealId: '',
			ticketType: '',
			ticketingRequested: null, // Limpiar completamente
			ticketingStatus: '', // Limpiar completamente
			saleOrderAgencyId: '',
			serviceEnable: null, // Limpiar completamente
			ticketingLimitDateFrom: null,
			ticketingLimitDateTo: null,
			serviceCreatedFrom: null,
			serviceCreatedTo: null,
		};
		setFilters(clearedFilters);
		onClearFilters();
	};

	const handleClearField = (field: keyof ModernFiltersState) => {
		const defaultValue =
			field === 'ticketingRequested' || field === 'serviceEnable' ? null : field.includes('Date') ? null : '';
		handleFilterChange(field, defaultValue);
	};

	const renderChip = (label: string, field: keyof ModernFiltersState, value: any) => {
		if (!value || value === '' || value === null) return null;

		let displayValue = value;
		if (field === 'ticketingStatus') {
			const option = ticketingStatusOptions.find((opt) => opt.id === value);
			displayValue = option?.name || value;
		} else if (field === 'ticketType') {
			const option = eticketTypeOptions.find((opt) => opt.id === value);
			displayValue = option?.name || value;
		} else if (field === 'saleOrderAgencyId') {
			const agency = countriesWithAgencyConfig.find((config) => config.agencyId.toString() === value.toString());
			displayValue = agency?.countryName || value;
		} else if (field === 'ticketingRequested') {
			displayValue = value ? 'Sí' : 'No';
		} else if (field === 'serviceEnable') {
			displayValue = value ? 'Habilitado' : 'Deshabilitado';
		} else if (field.includes('Date') && value instanceof Date) {
			displayValue = value.toLocaleDateString('es-ES');
		}

		return (
			<Chip
				key={field}
				label={`${label}: ${displayValue}`}
				onDelete={() => handleClearField(field)}
				color="primary"
				variant="outlined"
				size="small"
				sx={{ mr: 1, mb: 0 }}
			/>
		);
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<Paper elevation={2} sx={{ p: 2, mb: 2, mt: 3 }}>
				{/* Header */}
				<Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
					<Box display="flex" alignItems="center" gap={1}>
						<Badge badgeContent={activeFiltersCount} color="primary">
							<FilterListIcon color="action" />
						</Badge>
						<Typography variant="subtitle1" fontWeight={500} component="h2">
							Filtros:
						</Typography>
					</Box>

					{/* Chips de filtros activos */}
					{activeFiltersCount > 0 && (
						<Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5} flex={1} justifyContent="left">
							{renderChip('Estado Emisión', 'ticketingStatus', filters.ticketingStatus)}
							{renderChip('Emisión Solicitada', 'ticketingRequested', filters.ticketingRequested)}
							{renderChip('Validating Carrier', 'vcCode', filters.vcCode)}
							{renderChip('Sale Order ID', 'saleOrderId', filters.saleOrderId)}
							{renderChip('Creado por', 'saleOrderCreatedBy', filters.saleOrderCreatedBy)}
							{renderChip('Deal ID', 'saleOrderDealId', filters.saleOrderDealId)}
							{renderChip('Tipo de Ticket', 'ticketType', filters.ticketType)}
							{renderChip('Agencia', 'saleOrderAgencyId', filters.saleOrderAgencyId)}
							{renderChip('Servicio', 'serviceEnable', filters.serviceEnable)}
							{renderChip('Límite Emisión Desde', 'ticketingLimitDateFrom', filters.ticketingLimitDateFrom)}
							{renderChip('Límite Emisión Hasta', 'ticketingLimitDateTo', filters.ticketingLimitDateTo)}
							{renderChip('Creado Desde', 'serviceCreatedFrom', filters.serviceCreatedFrom)}
							{renderChip('Creado Hasta', 'serviceCreatedTo', filters.serviceCreatedTo)}
						</Box>
					)}

					<Box display="flex" gap={1}>
						<Tooltip title="Limpiar todos los filtros">
							<Button size="small" startIcon={<ClearIcon />} onClick={handleClearAll} variant="outlined" color="error">
								Limpiar
							</Button>
						</Tooltip>
						<IconButton onClick={() => setExpanded(!expanded)} size="small">
							{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
						</IconButton>
					</Box>
				</Box>

				{/* Chips de filtros activos */}

				{/* Formulario de filtros expandible */}
				<Collapse in={expanded}>
					<Divider sx={{ mb: 2 }} />
					<Grid container spacing={2}>
						{/* Primera fila - Filtros principales */}
						<Grid item xs={12} sm={6} md={3}>
							<FormControl fullWidth size="small">
								<InputLabel>Estado de Emisión</InputLabel>
								<Select
									value={filters.ticketingStatus}
									onChange={(e) => handleFilterChange('ticketingStatus', e.target.value)}
									label="Estado de Emisión"
								>
									{ticketingStatusOptions.map((option) => (
										<MenuItem key={option.id} value={option.id}>
											{option.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>

						<Grid item xs={12} sm={6} md={3}>
							<FormControlLabel
								control={
									<Switch
										checked={filters.ticketingRequested ?? false}
										onChange={(e) => handleFilterChange('ticketingRequested', e.target.checked)}
										color="primary"
									/>
								}
								label="Emisión Solicitada"
							/>
						</Grid>

						<Grid item xs={12} sm={6} md={3}>
							<FormControlLabel
								control={
									<Switch
										checked={filters.serviceEnable ?? false}
										onChange={(e) => handleFilterChange('serviceEnable', e.target.checked)}
										color="primary"
									/>
								}
								label="Servicio Habilitado"
							/>
						</Grid>

						<Grid item xs={12} sm={6} md={3}>
							<FormControl fullWidth size="small">
								<InputLabel>Tipo de Ticket</InputLabel>
								<Select
									value={filters.ticketType}
									onChange={(e) => handleFilterChange('ticketType', e.target.value)}
									label="Tipo de Ticket"
								>
									<MenuItem value="">
										<em>Todos</em>
									</MenuItem>
									{eticketTypeOptions.map((option) => (
										<MenuItem key={option.id} value={option.id}>
											{option.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>

						<Grid item xs={12} sm={6} md={3}>
							<TextField
								fullWidth
								size="small"
								label="Validating Carrier"
								value={filters.vcCode}
								onChange={(e) => handleFilterChange('vcCode', e.target.value)}
								placeholder="Ej: LA, AR"
							/>
						</Grid>

						{/* Segunda fila - IDs y referencias */}
						<Grid item xs={12} sm={6} md={4}>
							<TextField
								fullWidth
								size="small"
								label="Sale Order ID"
								value={filters.saleOrderId}
								onChange={(e) => handleFilterChange('saleOrderId', e.target.value)}
								placeholder="ID de orden de venta"
							/>
						</Grid>

						<Grid item xs={12} sm={6} md={4}>
							<TextField
								fullWidth
								size="small"
								label="Deal ID"
								value={filters.saleOrderDealId}
								onChange={(e) => handleFilterChange('saleOrderDealId', e.target.value)}
								placeholder="ID del deal"
								type="number"
							/>
						</Grid>

						<Grid item xs={12} sm={6} md={4}>
							<FormControl fullWidth size="small">
								<InputLabel>Agencia</InputLabel>
								<Select
									value={filters.saleOrderAgencyId}
									onChange={(e) => handleFilterChange('saleOrderAgencyId', e.target.value)}
									label="Agencia"
								>
									<MenuItem value="">
										<em>Todas</em>
									</MenuItem>
									{countriesWithAgencyConfig.map((config) => (
										<MenuItem key={config.agencyId} value={config.agencyId.toString()}>
											{config.countryName}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>

						{/* Tercera fila - Usuario */}
						<Grid item xs={12} sm={6} md={6}>
							<TextField
								fullWidth
								size="small"
								label="Creado por"
								value={filters.saleOrderCreatedBy}
								onChange={(e) => handleFilterChange('saleOrderCreatedBy', e.target.value)}
								placeholder="Nombre del usuario"
							/>
						</Grid>

						{/* Cuarta fila - Fechas */}
						<Grid item xs={12}>
							<Typography variant="subtitle2" color="text.secondary" mb={1}>
								Límite de Emisión
							</Typography>
						</Grid>
						<Grid item xs={12} sm={6}>
							<DatePicker
								label="Fecha desde"
								value={filters.ticketingLimitDateFrom}
								onChange={(date) => handleFilterChange('ticketingLimitDateFrom', date)}
								renderInput={(params) => <TextField {...params} size="small" fullWidth />}
								inputFormat="dd/MM/yyyy"
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<DatePicker
								label="Fecha hasta"
								value={filters.ticketingLimitDateTo}
								onChange={(date) => handleFilterChange('ticketingLimitDateTo', date)}
								renderInput={(params) => <TextField {...params} size="small" fullWidth />}
								inputFormat="dd/MM/yyyy"
							/>
						</Grid>

						<Grid item xs={12}>
							<Typography variant="subtitle2" color="text.secondary" mb={1}>
								Fecha de Creación del Servicio
							</Typography>
						</Grid>
						<Grid item xs={12} sm={6}>
							<DatePicker
								label="Fecha desde"
								value={filters.serviceCreatedFrom}
								onChange={(date) => handleFilterChange('serviceCreatedFrom', date)}
								renderInput={(params) => <TextField {...params} size="small" fullWidth />}
								inputFormat="dd/MM/yyyy"
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<DatePicker
								label="Fecha hasta"
								value={filters.serviceCreatedTo}
								onChange={(date) => handleFilterChange('serviceCreatedTo', date)}
								renderInput={(params) => <TextField {...params} size="small" fullWidth />}
								inputFormat="dd/MM/yyyy"
							/>
						</Grid>
					</Grid>
				</Collapse>
			</Paper>
		</LocalizationProvider>
	);
};
